/**
 * PastLivesRevealEntrance — entrance screen between loading and the carousel.
 *
 * Mirrors the avatarFlow RevealEntrance composition (kicker + italic title +
 * triple stack + dots + tap hint). Voice is past-lives specific:
 * "The chronicles have spoken — N lives await". Hero card (deliveryIndex 0,
 * isHero=true) gets the centered, badge-decorated slot.
 *
 * Auto-advances after `durationMs` (default 4000ms); tap also advances.
 */
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { PastLivesCard } from '../../hooks/usePastLivesRun';
import { CosmosBackdrop } from '../brand/CosmosBackdrop';
import styles from './PastLivesRevealEntrance.module.css';

export interface PastLivesRevealEntranceProps {
  cards: PastLivesCard[];
  onComplete: () => void;
  /** Auto-advance after N ms. Tap also advances. Default 4000 ms. */
  durationMs?: number;
  testId?: string;
}

const STACK_GRADIENTS = [
  'linear-gradient(150deg, rgba(232,154,60,0.62), rgba(122,61,255,0.45), rgba(13,8,36,0.95))',
  'linear-gradient(150deg, rgba(240,200,80,0.62), rgba(184,107,40,0.40), rgba(13,8,36,0.95))',
  'linear-gradient(150deg, rgba(122,61,255,0.55), rgba(59,91,255,0.40), rgba(13,8,36,0.95))',
];

export function PastLivesRevealEntrance({
  cards,
  onComplete,
  durationMs = 4000,
  testId = 'past-lives-reveal-entrance',
}: PastLivesRevealEntranceProps): JSX.Element {
  const { t } = useTranslation('flows');
  const completedRef = useRef(false);

  const total = cards.length;
  const hero = cards.find((c) => c.isHero) ?? cards[0] ?? null;
  const secondary = cards.filter((c) => c !== hero).slice(0, 2);

  useEffect(() => {
    const id = window.setTimeout(() => {
      if (!completedRef.current) {
        completedRef.current = true;
        onComplete();
      }
    }, durationMs);
    return () => window.clearTimeout(id);
  }, [onComplete, durationMs]);

  function trigger(): void {
    if (completedRef.current) return;
    completedRef.current = true;
    onComplete();
  }

  const stackTiles = useMemo(() => {
    return [
      { card: secondary[0] ?? null, rotateDeg: -7, offsetX: -22, offsetY: -10, z: 1 },
      { card: secondary[1] ?? null, rotateDeg: 7,  offsetX:  22, offsetY: -8,  z: 1 },
      { card: hero,                  rotateDeg: 0,  offsetX:   0, offsetY:  0,  z: 2, badge: 'HERO' as const },
    ];
  }, [hero, secondary]);

  return (
    <button
      type="button"
      className={styles.root}
      data-testid={testId}
      onClick={trigger}
      aria-label={t('pastLives.reveal.tapToContinue', {
        defaultValue: 'Tap to walk through your past lives',
      })}
    >
      <CosmosBackdrop />
      <div className={styles.wash} aria-hidden="true" />

      <div className={styles.content}>
        <div className={styles.kicker}>
          {t('pastLives.reveal.kicker', { defaultValue: 'The chronicles · ready' })}
        </div>

        <h1 className={styles.title}>
          {t('pastLives.reveal.titlePrefix', {
            count: total,
            defaultValue: '{{count}} past lives,',
          })}{' '}
          <em className={styles.titleAccent}>
            {t('pastLives.reveal.titleSuffix', {
              defaultValue: 'remembered for you',
            })}
          </em>
        </h1>

        <div className={styles.stack} aria-hidden="true">
          {stackTiles.map((tile, i) => (
            <div
              key={i}
              className={styles.stackTile}
              style={{
                transform: `translate(${tile.offsetX}px, ${tile.offsetY}px) rotate(${tile.rotateDeg}deg)`,
                opacity: i === 2 ? 1 : 0.62,
                zIndex: tile.z,
                background: tile.card?.portraitUrl ? undefined : STACK_GRADIENTS[i % 3],
              }}
            >
              {tile.card?.portraitUrl ? (
                <img src={tile.card.portraitUrl} alt="" className={styles.stackImg} />
              ) : (
                <span className={styles.stackGlyph}>✦</span>
              )}
              {tile.badge ? <span className={styles.stackBadge}>{tile.badge}</span> : null}
            </div>
          ))}
        </div>

        <p className={styles.subtitle}>
          {t('pastLives.reveal.subtitle', {
            defaultValue:
              'The Akashic records have opened. Each face is a thread of who you have been — swipe to walk through them.',
          })}
        </p>

        <div className={styles.dots} aria-hidden="true">
          {Array.from({ length: total }).map((_, i) => (
            <span key={i} className={i === 0 ? styles.dotHead : styles.dot} />
          ))}
        </div>

        <div className={styles.tapHint}>
          {t('pastLives.reveal.tapHint', {
            defaultValue: 'Tap to begin the walk →',
          })}
        </div>
      </div>
    </button>
  );
}

export default PastLivesRevealEntrance;
